import { Stack, Text } from "@certn/cascadia";
import { Select, SelectItem } from "@certn/cascadia-react";

import { Key } from "react-aria-components";
import { CustomQuestionnaireTemplateQuestionFieldProps } from "./CustomQuestionnaireQuestionField";
import { useTranslation } from "react-i18next";
import { getConditionalQuestions, getConditionValueOptions } from "./utils";

type CustomQuestionnaireConditionalLogicProps = Omit<
  CustomQuestionnaireTemplateQuestionFieldProps,
  "setValues"
>;

export const CustomQuestionnaireConditionalLogic = ({
  currentQuestion,
  index,
  values,
  setFieldValue,
  type,
}: CustomQuestionnaireConditionalLogicProps) => {
  const { t } = useTranslation();

  const conditions = currentQuestion.display_conditions ?? [];

  const currentCondition = conditions[0];
  const currentParentId = currentCondition?.parent_question_id;
  const selectedParentKey = currentParentId ?? "";
  const currentConditionValue = currentCondition?.condition_value;

  const selectedParent = currentParentId
    ? values.questions.find((q) => q.id === currentParentId)
    : undefined;

  const conditionValueOptions = selectedParent
    ? getConditionValueOptions(selectedParent, t)
    : currentConditionValue
      ? [
          {
            value: currentConditionValue,
            label: currentConditionValue.toUpperCase(),
          },
        ]
      : [];

  return (
    <Stack gap="space-4">
      <Text style="form/section-heading">{t("Show this question when:")}</Text>
      <Select
        isRequired
        label=""
        testID={`question-condition-parent-${index}`}
        isDisabled={type === "update"}
        value={selectedParentKey}
        onSelectionChange={(key: Key | null) => {
          const parentId = key == null ? "" : String(key);
          if (!parentId) {
            setFieldValue(`questions.${index}.display_conditions`, []);
          } else {
            setFieldValue(`questions.${index}.display_conditions`, [
              {
                parent_question_id: parentId,
                condition_value: null,
              },
            ]);
          }
        }}
      >
        {getConditionalQuestions(values.questions, currentQuestion).map(
          (question) => (
            <SelectItem key={question.id} id={question.id}>
              {t("{{number}}: {{question}}", {
                number: question.display_priority,
                question: question.external_name,
              })}
            </SelectItem>
          )
        )}
      </Select>
      <Select
        isDisabled
        value={t("Equals")}
        testID={`question-condition-operator-${index}`}
      >
        <SelectItem key={t("Equals")} id={t("Equals")}>
          {t("Equals")}
        </SelectItem>
      </Select>
      <Select
        isRequired
        label=""
        testID={`question-condition-value-${index}`}
        value={currentConditionValue}
        isDisabled={type === "update"}
        onSelectionChange={(key: Key | null) => {
          if (key == null || !currentParentId) return;
          setFieldValue(`questions.${index}.display_conditions`, [
            {
              parent_question_id: currentParentId,
              condition_value: String(key),
            },
          ]);
        }}
      >
        {conditionValueOptions.map((option) => (
          <SelectItem key={option.value} id={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </Select>
    </Stack>
  );
};
